import { FC, lazy, Suspense, useEffect, useState } from 'react';
const ReactApexChart = lazy(() => import('react-apexcharts'));
import type { ApexOptions } from 'apexcharts';

interface DataItem {
    name?: string | null;
    revenue?: number | null;
}

interface PiChartProps {
    data?: DataItem[];
    isLoading?: boolean;
    surFix?: string;
}

const randomNeonColor = () => {
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 100%, 60%)`;
};

const getRandomNeonColors = (count: number) =>
    Array.from({ length: count }, randomNeonColor);

const PiChart: FC<PiChartProps> = ({
    data = [],
    isLoading = false,
    surFix = '',
}) => {
    const [chartConfig, setChartConfig] = useState<{
        series: number[];
        options: ApexOptions;
    }>({
        series: [],
        options: {
            chart: {
                id: 'pie-chart',
                type: 'pie',
                height: 350,
            },
            labels: [],
            colors: [],
            dataLabels: {
                enabled: true,
                formatter: (val: number) => `${val.toFixed(1)}%`,
                style: {
                    fontWeight: 600,
                },
            },
            tooltip: {
                y: {
                    formatter: (val: number) => `${val} ${surFix}`,
                },
            },
            legend: {
                position: 'right',
            },
            stroke: {
                show: true,
                width: 1,
            },
        },
    });

    useEffect(() => {
        if (!data || data.length === 0) {
            setChartConfig((prev) => ({
                ...prev,
                series: [],
                options: {
                    ...prev.options,
                    labels: [],
                    colors: [],
                },
            }));
            return;
        }

        const labels = data.map((d) => d?.name ?? '');
        const values = data.map((d) => d?.revenue ?? 0);
        const neonColors = getRandomNeonColors(values.length);

        setChartConfig((prev) => ({
            ...prev,
            series: values,
            options: {
                ...prev.options,
                labels,
                colors: neonColors,
            },
        }));
    }, [data]);

    return (
        <div id="pie-chart" className="w-full">
            {isLoading ? (
                <div className="flex justify-center items-center h-[350px]">
                    <div className="animate-spin rounded-full h-10 w-10 "></div>
                </div>
            ) : data && data.length > 0 ? (
                <Suspense
                    fallback={
                        <div className="h-[350px] flex items-center justify-center">
                            Loading chart…
                        </div>
                    }
                >
                    <ReactApexChart
                        key={JSON.stringify(chartConfig.series)}
                        options={chartConfig.options}
                        series={chartConfig.series}
                        type="pie"
                        height={350}
                    />
                </Suspense>
            ) : (
                <div className="flex justify-center items-center h-[350px] rounded-lg bg-gray-50 text-gray-500">
                    No data available
                </div>
            )}
        </div>
    );
};

export default PiChart;
